import * as React from 'react';
import { getSimilarItemsUrl, getUpcomingTitleSearchParams } from '@/utils/urls';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import { CategoryLink } from '@/types/CategoryLink';
import classNames from 'classnames';
import DiscoveryTags from '@/components/DiscoveryTags/DiscoveryTags';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import { isUserLoggedIn } from '@/redux/modules/user';
import { ItemFacets } from '@/types/ItemFacets';
import ItemGrid from '@/components/ItemCard/ItemGrid';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import SeeAllLink from '../SeeAllLink/SeeAllLink';
import styles from './SimilarItems.module.scss';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import { useSelector } from 'react-redux';

type Props = {
    catalogStatus: string;
    categoryLinks: CategoryLink[];
    centerHeader: boolean;
    className: string;
    facets: ItemFacets;
    getMoreSimilarItems: Function;
    getTagItems: Function;
    isLoading: boolean;
    itemTitle: string;
    moreResultsAvailable: boolean;
    popularItemIds: number[];
    selectedItems: any;
    suggestions: any[];
    title?: React.ReactNode;
    useSimilarItemCard: boolean;
    width: number;
};

const SimilarItems = (props: Props) => {
    const {
        catalogStatus,
        categoryLinks,
        centerHeader = false,
        className = '',
        facets,
        getMoreSimilarItems,
        getTagItems,
        isLoading,
        itemTitle,
        moreResultsAvailable,
        // popularItemIds, // TODO: What is happening with these ids?
        selectedItems,
        suggestions,
        title,
        useSimilarItemCard,
        width,
    } = props;

    const userLoggedIn = useSelector(isUserLoggedIn);
    const classes = classNames(styles.similarItems, className);
    const gridHeaderClasses = classNames(styles.similarItemsHeader, { [styles.centerText]: centerHeader });
    const similarItemsUrl = getSimilarItemsUrl(itemTitle);
    const saveSearchParams = getUpcomingTitleSearchParams(itemTitle);
    saveSearchParams.status = 'online';

    let linkMessage;
    let headerMessage;

    if (selectedItems.length <= 0) {
        linkMessage = <FormattedMessage id="see_all" />;
        headerMessage = <FormattedMessage id="bid_most_popular_items" />;
    } else {
        headerMessage = title || <FormattedMessage id="discover_similar_items" />;
        linkMessage = (
            <>
                <FormattedMessage id="see_all" />
            </>
        );
    }

    const saveSearchContainer = (
        <SaveSearchContainer
            allSearchParams={saveSearchParams}
            className={styles.saveButton}
            primary={false}
            saveSearchParams={saveSearchParams}
            small
        />
    );

    const showSimilarItemCard = !userLoggedIn && catalogStatus === 'done' && useSimilarItemCard;

    return (
        <div className={classes}>
            <div className={gridHeaderClasses} key="header">
                <h2>{headerMessage}</h2>
                <div className={styles.saveSimilarSearchContainer}>
                    {saveSearchContainer}
                    <SeeAllLink text={linkMessage} url={similarItemsUrl} />
                </div>
            </div>
            <DiscoveryTags
                categoryLinks={categoryLinks}
                facets={facets}
                getTagItems={getTagItems}
                key="discoveryTags"
                suggestions={suggestions}
                width={width}
            />
            {isLoading ? (
                <Throbber />
            ) : (
                <ItemGrid
                    hideLotNumbers
                    hideSeller
                    itemCardClass={styles.similarCards}
                    itemIds={selectedItems}
                    key="grid"
                    showSubMenuTrigger={userLoggedIn}
                    useSimilarItemCard={showSimilarItemCard}
                />
            )}
            {moreResultsAvailable && (
                <Button className={styles.loadMoreButton} onClick={getMoreSimilarItems}>
                    <FormattedMessage id="loadMore" />
                </Button>
            )}
        </div>
    );
};

export default React.memo<Props>(SimilarItems, isEqual);
